import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

export interface DrinkPricingTier {
  priceTrial?: number;
  priceWeekly?: number;
  priceMonthly?: number;
  priceNonOffer?: number;
}

export interface PlanAddon {
  _id?: string;
  name: string;
  description?: string;
  price: number;
  quantity?: string;
  isActive: boolean;
  image?: string;
}

export interface Plan {
  program?: 'standard' | 'seed_cycling';
  seedCyclingLaunchReady?: boolean;
  seedCyclingTestPricing?: boolean;
  _id: string;
  title: string;
  description: string;
  price: number;
  priceWeekly: number;
  priceMonthly: number;
  priceNonOffer: number;
  priceLight?: number;
  priceWeeklyLight?: number;
  priceMonthlyLight?: number;
  priceNonOfferLight?: number;
  image?: string;
  duration: number;
  features: string[];
  isActive: boolean;
  isEnabled: boolean;
  ifPortionsAvailable?: boolean;
  planType: "meal" | "drink" | "meal_and_drink";
  drinkPricing?: {
    coffee?: DrinkPricingTier;
    blend?: DrinkPricingTier;
  };
  calorieRangeLight?: string;
  calorieRangeClassic?: string;
  isBestseller?: boolean;
  sortOrder?: number;
  badgeText?: string;
  addOns?: PlanAddon[];
  createdAt?: string;
  updatedAt?: string;
}

const getAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  },
});

export type SeedCatalogueSlot = { serialNumber: number; mealId: string | null; revision: number; name: string; recipeName: string; skipped: boolean; status: string; issues: string[] };
export type SeedReadiness = { ready: boolean; catalogueReady: boolean; protocolVersion: string; counts: { active: number; skipped: number; ready: number; needsSetup: number }; slots: SeedCatalogueSlot[]; recipes: { name: string; flax_pumpkin: string; sunflower_sesame: string }[] };

const plansApi = {
  setSeedMealActivity: async (slot: SeedCatalogueSlot): Promise<void> => {
    await axios.patch(`${API_URL}/api/admin/meals/${slot.mealId}/seed-cycling/availability`, { isActive: slot.skipped, revision: slot.revision }, getAuthHeaders());
  },
  createSeedCatalogueMeal: async (data: { serialNumber: number; name: string; description: string; imageUrl: string }): Promise<void> => {
    await axios.post(`${API_URL}/api/admin/meals/seed-cycling/catalogue`, data, getAuthHeaders());
  },
  getSeedReadiness: async (): Promise<SeedReadiness> => (await axios.get(`${API_URL}/api/admin/plans/seed-cycling/readiness`, getAuthHeaders())).data,
  getPlans: async (params?: { includeInactive?: boolean; all?: boolean }): Promise<Plan[]> => {
    const response = await axios.get(`${API_URL}/api/admin/plans`, {
      params: { includeInactive: true, ...params },
      ...getAuthHeaders(),
    });
    return response.data;
  },

  createPlan: async (data: Partial<Plan>): Promise<Plan> => {
    const response = await axios.post(`${API_URL}/api/admin/plans`, data, getAuthHeaders());
    return response.data;
  },

  updatePlan: async (id: string, data: Partial<Plan>): Promise<Plan> => {
    const response = await axios.put(`${API_URL}/api/admin/plans/${id}`, data, getAuthHeaders());
    return response.data;
  },

  patchPlan: async (id: string, data: Partial<Plan>): Promise<Plan> => {
    const response = await axios.patch(`${API_URL}/api/admin/plans/${id}`, data, getAuthHeaders());
    return response.data;
  },

  togglePlanActive: async (id: string, currentStatus: boolean): Promise<Plan> => {
    const response = await axios.patch(
      `${API_URL}/api/admin/plans/${id}`,
      { isActive: !currentStatus },
      getAuthHeaders()
    );
    return response.data;
  },

  togglePlanEnabled: async (id: string, currentStatus: boolean): Promise<Plan> => {
    const response = await axios.patch(
      `${API_URL}/api/admin/plans/${id}`,
      { isEnabled: !currentStatus },
      getAuthHeaders()
    );
    return response.data;
  },

  deletePlan: async (id: string): Promise<void> => {
    await axios.delete(`${API_URL}/api/admin/plans/${id}`, getAuthHeaders());
  },
};

export default plansApi;
